import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { PackageDetails } from 'ts/package/interfaces/PackageDetails';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faExclamationTriangle, faGear } from '@fortawesome/free-solid-svg-icons';
import { faFileLines, faImage, faMap } from '@fortawesome/free-regular-svg-icons';
import RoomsDetails from './RoomsDetails';
import HotelDescription from './HotelDescription';
import HotelFacilities from './HotelFacilities';
import HotelGallery from './HotelGallery';
import DetailsMap from './DetailsMap';
import Button from 'components/ui/Button/Button';
import { useSearchParams } from 'react-router-dom';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import RoomsDetails2 from './RoomDetails2';
import AdditionalDetails from './AdditionalDetails';
import { MainContext } from 'contexts/MainContext';
import { isNetravel, isTT } from 'constants/generic';
import Tabs from 'components/ui/Tabs/Tabs';

interface HotelDetailsProps {
  dealModel: PackageDetails;
  onChangePax: (
    totalPrice: number,
    totalAvgPrice: number,
    rooms: string[],
    infants: string[],
    infantsCount: number
  ) => void;
  totalPrice: number;
  onClickBook: () => void;
  rooms: string[];
  infants: string[];
  cmsPage?: CmsPageResponse;
}

const HotelDetails: FC<HotelDetailsProps> = ({ dealModel, rooms, infants, ...props }) => {
  const [t] = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [urlParams] = useSearchParams();
  const { setProposal, proposal } = useContext(MainContext);
  const [isLayout2, setIsLayout2] = useState(false);

  useEffect(() => {
    if (props.cmsPage?.pageItems?.length) {
      setIsLayout2(
        props.cmsPage.pageItems.find((item) => item.cmsAttribute === 'PriceView')
          ?.attrubuteLayout === 'Layout_2'
      );
    }
  }, [props.cmsPage]); // eslint-disable-line

  const onChangePax = (
    rooms: string[],
    totalPrice: number,
    totalAvgPrice: number,
    infants: string[],
    infantsCount: number
  ) => {
    props.onChangePax(totalPrice, totalAvgPrice, rooms, infants, infantsCount);
  };

  const onClickProposal = () => {
    if (!proposal || !setProposal || !rooms?.[0]?.length) return;

    const existingProposalIndex = proposal.ilPackages.findIndex(
      (proposal) => proposal.item.deal.dealId === dealModel.deal.dealId
    );

    if (existingProposalIndex !== -1) {
      const updatedPackages = proposal.ilPackages.filter(
        (_, index) => index !== existingProposalIndex
      );
      setProposal({ ...proposal, ilPackages: updatedPackages });
    } else {
      const newProposal = {
        item: JSON.parse(JSON.stringify(dealModel)),
        currency: dealModel.deal.currency,
        price: props.totalPrice,
        displayPrice: true,
        searchToken: urlParams.get('searchToken') || '',
        selectedRooms: rooms,
        type: urlParams.get('type') || '',
        selectedInfantInRooms: infants,
      };
      setProposal({ ...proposal, ilPackages: [...proposal.ilPackages, newProposal] });
    }
  };

  return (
    <Fragment>
      <div className="default-shadow flight-details-item position-relative">
        <div className="title-header-grey" style={{ paddingBottom: 0 }}>
          <Tabs
            label={t('hotel.desc')}
            className="title-header-grey"
            value={String(activeTab)}
            onValueChange={(v) => setActiveTab(Number(v))}
            items={[
              {
                value: '0',
                label: (
                  <>
                    <FontAwesomeIcon icon={faBed} className="mr-5" />
                    {t('hotel.rooms_prices', {
                      count: dealModel.options.length && dealModel.options[0]?.prices?.length,
                    })}
                  </>
                ),
              },
              {
                value: '1',
                label: (
                  <>
                    <FontAwesomeIcon icon={faFileLines as any} className="mr-5" />
                    {t('hotel.desc')}
                  </>
                ),
              },
              {
                value: '2',
                label: (
                  <>
                    <FontAwesomeIcon icon={faGear} className="mr-5" />
                    {t('hotel.fac')}
                  </>
                ),
              },
              {
                value: '3',
                label: (
                  <>
                    <FontAwesomeIcon icon={faImage as any} className="mr-5" />
                    {t('hotel.gallery')}
                  </>
                ),
              },
              {
                value: '4',
                label: (
                  <>
                    <FontAwesomeIcon icon={faMap as any} className="mr-5" />
                    {t('map.map')}
                  </>
                ),
              },
              ...(isNetravel
                ? [
                    {
                      value: '6',
                      label: (
                        <>
                          <FontAwesomeIcon icon={faExclamationTriangle as any} className="mr-5" />
                          תנאי ביטול
                        </>
                      ),
                    },
                    {
                      value: '5',
                      label: (
                        <>
                          <FontAwesomeIcon icon={faFileLines as any} className="mr-5" />
                          {t('notes')}
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />
          <ProposalHeart
            active={
              !!proposal?.ilPackages.some((prop) => prop.item.deal.dealId === dealModel.deal.dealId)
            }
            onClickPropsal={onClickProposal}
            disabledText={!rooms?.[0]?.length ? t('proposal.please_select_rooms') : ''}
          />
        </div>

        {activeTab === 0 && !isLayout2 && (
          <RoomsDetails
            dealModel={dealModel}
            onChangePax={onChangePax}
            rooms={rooms}
            infants={infants}
          />
        )}
        {activeTab === 0 && isLayout2 && (
          <RoomsDetails2
            dealModel={dealModel}
            onChangePax={onChangePax}
            rooms={rooms}
            infants={infants}
          />
        )}
        {activeTab === 1 && <HotelDescription dealModel={dealModel} />}
        {activeTab === 2 && <HotelFacilities dealModel={dealModel} />}
        {activeTab === 3 && <HotelGallery dealModel={dealModel} />}
        {activeTab === 4 && (
          <DetailsMap
            lat={+dealModel.info.hotelLocation.latitude}
            lng={+dealModel.info.hotelLocation.longitude}
            zoom={17}
          />
        )}
        {activeTab === 5 && <AdditionalDetails dealModel={dealModel} showTopCss />}
        {activeTab === 6 && props.cmsPage && (
          <div className="text-lh text-st text-black">
            <div className="title text-xl">
              {props.cmsPage.page.languages.find((la) => la.language === 'He')?.title_2.desc}
            </div>
            <div
              dangerouslySetInnerHTML={{
                __html:
                  props.cmsPage.page.languages.find((la) => la.language === 'He')?.title_2.body ||
                  '',
              }}
            />
          </div>
        )}
      </div>
      <div className="flex-row-end">
        <Button
          className="btn btn-primary btn-lg"
          title={t(isTT ? 'continue' : 'hotel.book')}
          onClick={props.onClickBook}
        />
      </div>
    </Fragment>
  );
};

export default withErrorBoundary(HotelDetails, 'HotelDetails');
